import { FC, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@new/common/ui/Button/Button';
import { useCreateChatMutation } from '@teachur/http-client';
import { toast } from 'react-toastify';
import { UserSelector } from './UserSelector';

interface NewChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NewChatModal: FC<NewChatModalProps> = ({ isOpen, onClose }) => {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [message, setMessage] = useState('');

  const createChat = useCreateChatMutation({
    onError: () => toast.error('Failed to create chat.'),
  });

  useEffect(() => {
    if (!isOpen) {
      setSelectedUserId(null);
      setMessage('');
    }
  }, [isOpen]);

  const onSubmit = () => {
    if (!selectedUserId || !message.trim()) return;

    createChat.mutate(
      { userId: selectedUserId, text: message.trim() },
      {
        onSuccess: () => {
          setSelectedUserId(null);
          setMessage('');
          onClose();
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-bold text-neutral-900">New Chat</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Select User</label>
            <UserSelector value={selectedUserId} onSelect={setSelectedUserId} />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              rows={4}
              className="w-full bg-neutral-100 rounded-lg px-4 py-3 text-sm outline-none placeholder:text-neutral-400 resize-none focus:ring-2 focus:ring-sky-300"
              disabled={!selectedUserId}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200">
          <Button
            onClick={onSubmit}
            variant="primary"
            disabled={!selectedUserId || !message.trim() || createChat.isPending}
          >
            {createChat.isPending ? 'Creating...' : 'Create Chat'}
          </Button>
        </div>
      </div>
    </div>
  );
};
